import { Component, OnInit, OnDestroy, inject, signal } from '@angular/core';
import { CommonModule } from '@angular/common';
import { Router } from '@angular/router';
import { firstValueFrom, catchError, of, Subject, Subscription } from 'rxjs';
import { TranslateModule } from '@ngx-translate/core';
import { MatIconModule } from '@angular/material/icon';
import { EnvironmentService } from '../../../shared/services/environment.service';
import { NotificationService } from '../../../shared/services/notification.service';
import { NotificationFilterService } from '../../../shared/services/notification-filter.service';
import { NotificationFilterComponent } from '../../../components/notificationFilter/notification-filter.component';
import { MockMessageData } from '../../../mocks/mock-message-data';
import { NotificationItem, NotificationType } from '../../../models/notification.model';

@Component({
  selector: 'app-messages-main-page',
  standalone: true,
  imports: [CommonModule, TranslateModule, MatIconModule, NotificationFilterComponent],
  templateUrl: './messagesMainPage.component.html',
  styleUrls: []
})
export class MessagesMainPageComponent implements OnInit, OnDestroy {
  private _router = inject(Router);
  private _environment = inject(EnvironmentService);
  private _notifService = inject(NotificationService);
  private _filterService = inject(NotificationFilterService);
  
  private destroy$ = new Subject<void>();
  private _pushSub?: Subscription;
  private _filterSub?: Subscription;

  allMessages: NotificationItem[] = [];
  loading = signal(true);
  error = '';

  async ngOnInit(): Promise<void> {
    this._filterService.reset();
    this._filterSub = this._filterService.currentFilter$.subscribe(() => {
      void this.loadMessages();
    });

    this._pushSub = this._notifService.pushReceived$.subscribe(event => {
      if (event.type === NotificationType.Msg) {
        console.log('[MessagesMainPage] Push received, reloading messages...');
        this.loadMessages();
      }
    });
  }

  get filteredMessages(): NotificationItem[] {
  const currentFilter = this._filterService?.getCurrentFilter?.() || 'all';
  
  switch (currentFilter) {
    case 'unread':
      return this.allMessages?.filter(m => !m.isRead)?.sort((a, b) => 
        new Date(b.created).getTime() - new Date(a.created).getTime()
      ) || this.allMessages || [];
    case 'all':
    default:
      return this.allMessages?.sort((a, b) => 
        new Date(b.created).getTime() - new Date(a.created).getTime()
      ) || this.allMessages || [];
  }
}

  ngOnDestroy(): void {
    this.destroy$.next();
    this.destroy$.complete();
    this._pushSub?.unsubscribe();
    this._filterSub?.unsubscribe();
  }

  async refreshMessages(): Promise<void> {
    await this.loadMessages();
  }

  async loadMessages(): Promise<void> {
    this.loading.set(true);
    this.error = '';

    console.log('[MessagesMainPage] Loading message notifications...');

    const messages$ = this._environment.isDesignMode
      ? of(this.loadMockMessages())
      : this.loadRealMessages();

    await firstValueFrom(
      messages$.pipe(
        catchError(error => {
          console.error('[MessagesMainPage] Error loading messages:', error);
          this.error = 'Error loading messages. Please try again.';
          return of([] as NotificationItem[]);
        })
      )
    ).then(messages => {
      this.allMessages = messages;
      console.log(`[MessagesMainPage] Loaded ${this.allMessages.length} messages`);
      
      this._filterService.setNotifications(this.allMessages);
    }).finally(() => {
      this.loading.set(false);
    });
  }

  private loadMockMessages(): NotificationItem[] {
    console.log('[MessagesMainPage] Using mock data (design mode)');
    return MockMessageData.getMessageList();
  }

  private loadRealMessages() {
    console.log('[MessagesMainPage] Using real API data');

    return this._notifService.getNotificationsByType(
      NotificationType.Msg,
      this._filterService.getCurrentIsReadFilter()
    ).pipe(
      catchError(error => {
        console.error('[MessagesMainPage] Error loading messages:', error);
        throw error;
      })
    );
  }

  onMessageClick(messageId: string | number): void {
    console.log('[MessagesMainPage] Clicked message ID:', messageId);

    const numericId = Number(messageId);
    if (!this.isValidMessageId(numericId)) {
      console.error('[MessagesMainPage] Invalid message ID:', messageId);
      return;
    }

    console.log('[MessagesMainPage] Navigating to message detail:', numericId);
    this._router.navigate(['/message/detail', numericId]);
  }

  private isValidMessageId(messageId: number): boolean {
    if (!messageId) {
      return false;
    }

    const numericId = Number(messageId);
    return !isNaN(numericId) && numericId > 0;
  }

  formatDate(dateString: string): string {
    if (!dateString) return '';

    return new Date(dateString).toLocaleString('cs-CZ', {
      day: '2-digit',
      month: '2-digit',
      year: 'numeric',
      hour: '2-digit',
      minute: '2-digit'
    });
  }

  getPreviewText(body: string, maxLength: number = 100): string {
    return this._notifService.getPreviewText(body, maxLength);
  }

  getReadStatusText(isRead: boolean): string {
    return this._notifService.getReadStatusText(isRead);
  }

  get messages(): NotificationItem[] {
    return this.allMessages;
  }
}
